import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

export const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqItems: FAQItem[] = [
    {
      question: "Le ROI affiché est-il fiable ?",
      answer: "Les calculs s'appuient sur vos données ADEME réelles — surface de murs, type de chauffage, déperditions par poste — et non sur des moyennes nationales. Pour chaque travaux, DPEWizard calcule les kWh économisés, les euros économisés par an sur vos factures, et le coût estimé de l'installation sur la base des prix constatés en France métropolitaine en 2025. Le retour sur investissement affiché est donc un ordre de grandeur fiable à 10-15% près selon votre région et votre prestataire — bien suffisant pour décider si un poste de travaux vaut la peine d'être approfondi."
    },
    {
      question: "Pourquoi DPEWizard affiche deux lettres DPE et pas une seule ?",
      answer: "Parce que depuis juillet 2021, le DPE officiel se base sur deux critères distincts : la consommation d'énergie primaire (kWhEP/m²/an) ET les émissions de gaz à effet de serre (kgCO2/m²/an). Chacun génère sa propre lettre (A à G). C'est la plus mauvaise des deux qui détermine votre classe finale. DPEWizard vous montre les deux lettres séparément pour que vous compreniez précisément sur quel levier agir : si vous êtes D en énergie mais F en GES, c'est votre mode de chauffage (fioul, gaz) qu'il faut changer en priorité."
    },
    {
      question: "Les nouveaux coefficients 2026 sont-ils pris en compte ?",
      answer: "Oui. Depuis le 1er janvier 2026, le coefficient de conversion de l'électricité est passé de 2,3 à 1,9 et le facteur d'émissions GES de l'électricité a été révisé de 79 à 65 gCO2/kWh. DPEWizard intègre ces nouveaux paramètres dans tous les calculs de scénarios. Concrètement, les pompes à chaleur et les équipements électriques sont mieux valorisés qu'avant — ce qui peut changer significativement la lettre GES projetée de votre bien."
    },
    {
      question: "Mon DPE date de plusieurs années — les calculs restent-ils valables ?",
      answer: "DPEWizard fonctionne uniquement avec les DPE établis selon la méthode 3CL, obligatoire depuis juillet 2021. Les DPE antérieurs à cette date utilisaient une méthode sur factures qui n'est plus opposable légalement — et qui n'est pas disponible via l'API ADEME. Si votre DPE date d'avant juillet 2021, une mise à jour s'impose de toute façon avant toute démarche de vente ou de location. À noter que l'ADEME met environ 2 semaines à rendre vos informations disponibles pour DPEWizard. Si le diagnostiqueur vient juste d'établir le DPE chez vous, il faudra patienter quelques jours."
    },
    {
      question: "Puis-je comparer plusieurs stratégies de travaux ?",
      answer: "Oui, c'est justement l'usage principal de DPEWizard. Une fois votre DPE analysé, vous accédez au simulateur interactif où vous pouvez cocher/décocher n'importe quelle combinaison de travaux (isolation des murs, changement de chaudière, fenêtres, VMC…). Chaque modification recalcule instantanément votre DPE projeté et le ROI correspondant. Vous pouvez ainsi tester des dizaines de scénarios (« Et si je fais juste les murs + la VMC ? », « Et si j'ajoute le changement de chaudière ? ») pour identifier la stratégie la plus rentable selon votre budget."
    },
    {
      question: "Combien de temps faut-il pour obtenir une analyse complète ?",
      answer: "L'analyse automatique de votre DPE prend quelques secondes. Ensuite, selon la complexité de votre bien, vous répondrez à 8 à 35 questions personnalisées. Au total, comptez 2 à 5 minutes pour obtenir votre rapport complet avec les incohérences détectées, les travaux recommandés et l'accès au simulateur de scénarios de travaux chiffrés."
    },
    {
      question: "Que se passe-t-il si mon DPE ne contient aucune erreur ?",
      answer: "Même avec un DPE correct, DPEWizard vous donne accès au simulateur de travaux pour tester l'impact de différentes améliorations énergétiques. Vous recevrez des recommandations personnalisées selon vos objectifs (vente, location, confort) et pourrez comparer les stratégies d'investissement."
    },
    {
      question: "Les aides financières sont-elles intégrées dans les calculs ?",
      answer: "Non, volontairement. Les aides disponibles — MaPrimeRénov', CEE, éco-PTZ, TVA réduite à 5,5% — dépendent de trop de variables personnelles pour être estimées avec précision : revenus, zone géographique, année de construction, cumul possible. Plutôt que d'afficher des chiffres approximatifs, DPEWizard vous oriente vers les simulateurs officiels de l'État pour obtenir une estimation fiable de votre reste à charge réel."
    },
    {
      question: "Combien puis-je vraiment économiser avec DPEWizard ?",
      answer: "Ça dépend de votre situation. Si votre DPE contient des incohérences — équipement sous-dimensionné, élément manquant, erreur de saisie — les corriger peut vous faire gagner une ou deux lettres sans aucun travaux. Si vous planifiez une rénovation, simuler les scénarios avant d'appeler des artisans vous évite de financer des postes peu rentables ou sans impact sur votre classe énergétique. Dans les deux cas, le coût de DPEWizard est négligeable par rapport aux décisions qu'il vous permet d'éclairer."
    },
    {
      question: "L'investissement vaut-il vraiment le coup ?",
      answer: "Un seul devis de travaux inutiles vous coûtera plus cher que DPEWizard. Pour une analyse unique, vous obtenez une cartographie complète de tous les leviers d'amélioration de votre bien, avec leur impact chiffré sur votre DPE et leur rentabilité. C'est l'équivalent d'une consultation avec un bureau d'études thermiques, mais instantanée, interactive et bien moins chère. Si votre DPE comporte des erreurs, l'économie sur la vente se chiffre en milliers d'euros. Si vous planifiez des travaux, vous évitez les mauvais choix qui coûtent des dizaines de milliers d'euros."
    }
  ];

  return (
    <section className="py-20 bg-gray-50" id="faq">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Titre avec style cohérent */}
        <div className="mb-16">
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 leading-tight uppercase">
            Questions fréquentes
          </h2>
          <div className="w-12 h-0.5 bg-blue-500 mb-6"></div>
          <p className="text-lg text-gray-600 max-w-4xl">
            Fiabilité des calculs, méthodologie, fonctionnalités et valeur ajoutée
          </p>
        </div>

        <div className="space-y-4 max-w-4xl mx-auto">
          {faqItems.map((item, index) => (
            <div 
              key={index}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden transition-all duration-200 shadow-sm"
            >
              <button
                className="w-full flex items-center justify-between p-6 text-left focus:outline-none hover:bg-gray-50 transition-colors"
                onClick={() => toggleQuestion(index)}
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <h3 className="text-lg font-medium text-gray-900">{item.question}</h3>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              <div 
                id={`faq-answer-${index}`}
                className={`px-6 overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 pb-6' : 'max-h-0'
                }`}
              >
                <p className="text-gray-600">{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;